import { formatCurrency } from '@/lib/formatters';
import type { LearnContext } from './types';

/** Dollars with cents — per-share money, distances to a strike. */
export const $ = (value: number) => formatCurrency(value);
/** Whole dollars — capital held, per-contract worst cases, where cents are noise. */
export const $0 = (value: number) => formatCurrency(value, { cents: false });
/** One decimal, the same precision the Risk view's safety colours are read at. */
export const pct = (value: number) => `${value.toFixed(1)}%`;
/**
 * A strike distance printed as a plain number. Strikes are decimals, so a bare subtraction can
 * yield 4.999999999999999 — round to cents before printing a width.
 */
export const width = (a: number, b: number) => Math.round(Math.abs(a - b) * 100) / 100;

/**
 * Where the position's payoff line sits at the live price, per share, if the stock finished
 * exactly there. Pure: the same arithmetic a risk graph draws, evaluated at one point.
 *
 * Every leg is optional, so each term is skipped when its strike is absent — a lone short put
 * keeps its open-ended loss below the strike rather than borrowing a wing it does not have.
 * Null when there is no price or no credit: a line with no vertical origin cannot be placed,
 * and guessing zero would print a confident wrong number.
 *
 * Rounded to cents for the same reason `width` is: 2.4 − 2.81 is −0.41000000000000014 in
 * binary floating point, and a payoff is money, not a float.
 */
export function payoffPerShare(ctx: LearnContext): number | null {
  const { price, credit, shortPut, longPut, shortCall, longCall } = ctx;
  if (price == null || credit == null) return null;
  const intrinsic = (strike: number | null, side: 'put' | 'call') =>
    strike == null ? 0 : Math.max(0, side === 'put' ? strike - price : price - strike);
  const payoff =
    credit -
    intrinsic(shortPut, 'put') +
    intrinsic(longPut, 'put') -
    intrinsic(shortCall, 'call') +
    intrinsic(longCall, 'call');
  return Math.round(payoff * 100) / 100;
}
